import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Platform } from 'interfaces/db';
import { Dispatch } from 'redux';
import { getServicesManager } from 'services';

interface PlatformDispositionsState {
    // Simple state structure
    currentDispositionOptions: Record<string, string[]>; // What user is currently editing
    savedDispositionOptions: Record<string, string[]>;   // What's saved on backend
    isLoading: Record<string, boolean>;                  // Loading per platform
    isSaving: Record<string, boolean>;                   // Saving per platform
    error: string | null;
    loadedPlatforms: string[];                           // Track which platforms we've loaded
}

const initialState: PlatformDispositionsState = {
    currentDispositionOptions: {},
    savedDispositionOptions: {},
    isLoading: {},
    isSaving: {},
    error: null,
    loadedPlatforms: [],
};

// Helper to convert string to array - supports both JSON and legacy comma-separated format
const stringToArray = (str: string): string[] => {
    if (!str || str.trim() === '') return [];

    // Try to parse as JSON first (new format) - supports commas in disposition names
    try {
        const parsed = JSON.parse(str);
        if (Array.isArray(parsed)) {
            return parsed.map(s => String(s).trim()).filter(s => s.length > 0);
        }
    } catch (e) {
        // Not JSON, fall back to comma-separated parsing (legacy format)
    }

    // Fallback: comma-separated string (legacy format for backwards compatibility)
    return str.split(',').map(s => s.trim()).filter(s => s.length > 0);
};

// Helper to convert array to JSON string (always use JSON format going forward)
const arrayToString = (arr: string[]): string => {
    const cleaned = arr.map(s => s.trim()).filter(s => s.length > 0);
    return JSON.stringify(cleaned);
};

const platformDispositionsSlice = createSlice({
    name: 'platformDispositions',
    initialState,
    reducers: {
        // Set loading state for a platform
        setLoading: (state, action: PayloadAction<{ platform: Platform; loading: boolean }>) => {
            state.isLoading[action.payload.platform] = action.payload.loading;
        },
        
        // Set saving state for a platform
        setSaving: (state, action: PayloadAction<{ platform: Platform; saving: boolean }>) => {
            state.isSaving[action.payload.platform] = action.payload.saving;
        },
        
        // Set error
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        
        // Load data from backend - sets both saved and current
        loadPlatformData: (state, action: PayloadAction<{ platform: Platform; dispositions: string }>) => {
            const dispositionsArray = stringToArray(action.payload.dispositions);
            state.savedDispositionOptions[action.payload.platform] = dispositionsArray;
            state.currentDispositionOptions[action.payload.platform] = [...dispositionsArray];
            if (!state.loadedPlatforms.includes(action.payload.platform)) {
                state.loadedPlatforms.push(action.payload.platform);
            }
            state.isLoading[action.payload.platform] = false;
        },
        
        // Update current (editing) dispositions
        updateCurrentDispositions: (state, action: PayloadAction<{ platform: Platform; dispositions: string[] }>) => {
            state.currentDispositionOptions[action.payload.platform] = action.payload.dispositions;
        },
        
        // After successful save - sync current to saved
        saveSuccess: (state, action: PayloadAction<{ platform: Platform; dispositions: string[] }>) => {
            state.savedDispositionOptions[action.payload.platform] = action.payload.dispositions;
            state.currentDispositionOptions[action.payload.platform] = [...action.payload.dispositions];
            state.isSaving[action.payload.platform] = false;
        },
        
        // Reset current to match saved
        resetToSaved: (state, action: PayloadAction<Platform>) => {
            const saved = state.savedDispositionOptions[action.payload] || [];
            state.currentDispositionOptions[action.payload] = [...saved];
        },
        
        // Clear all for platform
        clearPlatform: (state, action: PayloadAction<Platform>) => {
            state.savedDispositionOptions[action.payload] = [];
            state.currentDispositionOptions[action.payload] = [];
        },
    },
});

export const {
    setLoading,
    setSaving,
    setError,
    loadPlatformData,
    updateCurrentDispositions,
    saveSuccess,
    resetToSaved,
    clearPlatform,
} = platformDispositionsSlice.actions;

// Selectors
export const selectIsLoading = (platform: Platform) => (state: any) => 
    state.platformDispositions.isLoading[platform] || false;

export const selectIsSaving = (platform: Platform) => (state: any) => 
    state.platformDispositions.isSaving[platform] || false;

export const selectCurrentDispositions = (platform: Platform) => (state: any) => 
    state.platformDispositions.currentDispositionOptions[platform] || [];

export const selectSavedDispositions = (platform: Platform) => (state: any) => 
    state.platformDispositions.savedDispositionOptions[platform] || [];

export const selectHasUnsavedChanges = (platform: Platform) => (state: any) => {
    const current = state.platformDispositions.currentDispositionOptions[platform] || [];
    const saved = state.platformDispositions.savedDispositionOptions[platform] || [];
    return JSON.stringify(current) !== JSON.stringify(saved);
};

export const selectIsLoaded = (platform: Platform) => (state: any) => 
    state.platformDispositions.loadedPlatforms.includes(platform);

export const selectError = (state: any) => state.platformDispositions.error;

// Async Actions
export const loadPlatformDispositions = async (dispatch: Dispatch, platform: Platform) => {
    dispatch(setLoading({ platform, loading: true }));
    dispatch(setError(null));
    
    try {
        const servicesManager = getServicesManager();
        const dispositions = await servicesManager.getPlatformDispositions(platform);
        const finalDispositions = dispositions || "";
        dispatch(loadPlatformData({ platform, dispositions: finalDispositions }));
    } catch (error) {
        dispatch(setError("Failed to load platform dispositions"));
        dispatch(setLoading({ platform, loading: false }));
    }
};

export const savePlatformDispositions = async (dispatch: Dispatch, platform: Platform, dispositions: string[]) => {
    dispatch(setSaving({ platform, saving: true }));
    dispatch(setError(null));
    
    try {
        const servicesManager = getServicesManager();
        const dispositionsString = arrayToString(dispositions);
        const success = await servicesManager.updatePlatformDispositions(platform, dispositionsString);
        
        if (success) {
            dispatch(saveSuccess({ platform, dispositions }));
        } else {
            dispatch(setError("Failed to save platform dispositions"));
            dispatch(setSaving({ platform, saving: false }));
        }
    } catch (error) {
        dispatch(setError("Failed to save platform dispositions"));
        dispatch(setSaving({ platform, saving: false }));
    }
};

export const clearPlatformDispositions = async (dispatch: Dispatch, platform: Platform) => {
    dispatch(setSaving({ platform, saving: true }));
    dispatch(setError(null));
    
    try {
        const servicesManager = getServicesManager();
        const success = await servicesManager.clearPlatformDispositions(platform);
        
        if (success) {
            dispatch(clearPlatform(platform));
            dispatch(setSaving({ platform, saving: false }));
        } else {
            dispatch(setError("Failed to clear platform dispositions"));
            dispatch(setSaving({ platform, saving: false }));
        }
    } catch (error) {
        dispatch(setError("Failed to clear platform dispositions"));
        dispatch(setSaving({ platform, saving: false }));
    }
};

export default platformDispositionsSlice.reducer;